import React, { useState, useEffect, useCallback } from "react";
import api from "../../../api/concessions-api";
import sheet from "../../../containers/AdminList/styles";
import "react-datepicker/dist/react-datepicker.css";
import SlideIn from "../../../components/Drawer";
import {
  List,
  ListItem,
  Typography,
  Button,
  TextField,
  CircularProgress,
  MenuItem,
  Select
} from "@material-ui/core";

import { getRequestError } from "../../../utils/functions";
import GoBack from "../../../components/Button/GoBack";
import { useDispatch } from "react-redux";
import types from "../../../redux/types";

import { CircularLoad } from "../../../components/Loader";

const ComboDealForm2 = ({
  onClose,
  handleChange,
  open,
  jwt,
  refreshList,
  close,
  data,
  cinemaId,
  type
}) => {
  console.log("Form data:", data);
  const [rosettaConcessions, setRosettaConcessions] = useState([]);
  const [loadingRosettaConcessions, setLoadingRosettaConcessions] = useState(
    false
  );
  const [error, setError] = useState("");
  const [editedValues, setEditedValues] = useState(data);
  const [loading, setLoading] = useState(false);
  const { ALERT_OPEN } = types;

  // New state variables for the enhanced functionality
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [movies, setMovies] = useState([]);
  const [selectedMovie, setSelectedMovie] = useState(null);
  const [showtimes, setShowtimes] = useState([]);
  const [selectedShowtime, setSelectedShowtime] = useState(null);
  const [ticketTypes, setTicketTypes] = useState([]);
  const [selectedTicketTypes, setSelectedTicketTypes] = useState([]);
  const [loadingMovies, setLoadingMovies] = useState(false);
  const [loadingTickets, setLoadingTickets] = useState(false);

  const dispatch = useDispatch();
  const { classes } = sheet;

  // Generate next 7 days including today
  const getNext7Days = () => {
    const dates = [];
    const today = new Date();
    for (let i = 0; i < 7; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push({
        value: date.toISOString().split("T")[0],
        label:
          i === 0
            ? "Today"
            : date.toLocaleDateString("en-US", {
                weekday: "short",
                month: "short",
                day: "numeric"
              })
      });
    }
    return dates;
  };

  const resetOnClose = e => {
    onClose(e);
  };

  // Initialize isOn based on whether there are attachments in edit mode
  const [isOn, setIsOn] = useState(false);
  const containerStyle = {
    width: "44px",
    height: "24px",
    borderRadius: "12px",
    border: "none",
    padding: 0,
    cursor: "pointer",
    position: "relative",
    backgroundColor: isOn ? "#00CC73" : "#ccc",
    transition: "background-color 0.3s ease-in-out"
  };
  const thumbStyle = {
    width: "20px",
    height: "20px",
    borderRadius: "50%",
    backgroundColor: "#fff",
    position: "absolute",
    top: "2px",
    left: isOn ? "22px" : "2px",
    transition: "left 0.3s ease-in-out, box-shadow 0.2s ease",
    boxShadow:
      "0 1.2px 2.4px rgba(16, 24, 40, 0.06), 0 1.2px 3.6px rgba(16, 24, 40, 0.1)"
  };

  const fetchRosettaConcessions = useCallback(() => {
    setLoadingRosettaConcessions(true);
    api
      .fetchRosettaConcessions(`Bearer ${jwt}`, cinemaId)
      .then(res => {
        console.log(res?.data?.data);
        setRosettaConcessions(
          res.data.data?.map(({ product_name, product_id, display_price }) => ({
            product_name,
            product_id,
            product_price: display_price
          }))
        );
        setLoadingRosettaConcessions(false);
      })
      .catch(e => {
        setLoadingRosettaConcessions(false);
      });
  }, [jwt, cinemaId]);

  // Fetch movies based on selected date
  const fetchMovies = useCallback(async () => {
    if (!selectedDate) return;

    setLoadingMovies(true);
    try {
      const response = await api.fetchMoviesForDate(
        `Bearer ${jwt}`,
        cinemaId,
        selectedDate,
        selectedDate
      );

      if (response.data.status && response.data.data) {
        setMovies(response.data.data);
      } else {
        setMovies([]);
      }
    } catch (error) {
      console.error("Error fetching movies:", error);
      setMovies([]);
      dispatch({
        type: ALERT_OPEN,
        payload: {
          severity: "error",
          message: "Failed to fetch movies"
        }
      });
    } finally {
      setLoadingMovies(false);
    }
  }, [selectedDate, cinemaId, jwt, dispatch, ALERT_OPEN]);

  // Fetch ticket types based on selected showtime
  const fetchTicketTypes = useCallback(async () => {
    if (!selectedShowtime) return;

    setLoadingTickets(true);
    try {
      const response = await api.fetchTicketTypes(
        cinemaId,
        selectedShowtime.show_time_id,
        selectedShowtime.cinema_screen_id
      );

      if (
        response.data.status &&
        response.data.data &&
        response.data.data.tickets
      ) {
        setTicketTypes(response.data.data.tickets);
      } else {
        setTicketTypes([]);
      }
    } catch (error) {
      console.error("Error fetching ticket types:", error);
      setTicketTypes([]);
      dispatch({
        type: ALERT_OPEN,
        payload: {
          severity: "error",
          message: "Failed to fetch ticket types"
        }
      });
    } finally {
      setLoadingTickets(false);
    }
  }, [selectedShowtime, cinemaId, dispatch, ALERT_OPEN]);

  useEffect(() => {
    fetchRosettaConcessions();
  }, [jwt, fetchRosettaConcessions, cinemaId]);

  useEffect(() => {
    if (isOn) {
      fetchMovies();
    }
  }, [selectedDate, isOn, fetchMovies]);

  useEffect(() => {
    if (selectedShowtime) {
      fetchTicketTypes();
    }
  }, [selectedShowtime, fetchTicketTypes]);

  // Enhanced useEffect to handle edit data population
  useEffect(() => {
    if (Object.keys(data).length > 0) {
      console.log("Processing edit data:", data);

      const editableItems = {
        item: data.item.product_id ?? null,
        price: data.price,
        items: data?.items?.map(item => item.product_id) ?? [],
        // Store the combo deal ID for editing
        combo_deal_id: data.combo_deal_data?.id ?? null
      };
      setEditedValues(editableItems);

      // Check if there are attachments and set the toggle accordingly
      const hasAttachments =
        (data.attached_movies && data.attached_movies.length > 0) ||
        (data.attached_showtimes && data.attached_showtimes.length > 0) ||
        (data.attached_tickets && data.attached_tickets.length > 0);

      if (hasAttachments) {
        setIsOn(true);

        // Set selected movie if exists
        if (data.attached_movies && data.attached_movies.length > 0) {
          const attachedMovie = data.attached_movies[0];
          // We'll need to wait for movies to load to set the selected movie
          // This will be handled in a separate useEffect
        }

        // Set selected showtime date if exists
        if (data.attached_showtimes && data.attached_showtimes.length > 0) {
          const attachedShowtime = data.attached_showtimes[0];
          const showtimeDate = new Date(attachedShowtime.showtime_start)
            .toISOString()
            .split("T")[0];
          setSelectedDate(showtimeDate);
        }

        // Set selected ticket types if exists
        if (data.attached_tickets && data.attached_tickets.length > 0) {
          const ticketIds = data.attached_tickets.map(
            ticket => ticket.ticket_id
          );
          setSelectedTicketTypes(ticketIds);
        }
      }
    } else {
      setEditedValues({
        item: null,
        price: "",
        items: [],
        combo_deal_id: null
      });
      setIsOn(false);
      setSelectedMovie(null);
      setSelectedShowtime(null);
      setSelectedTicketTypes([]);
      setSelectedDate(new Date().toISOString().split("T")[0]);
    }
    setLoading(false);
  }, [data]);

  // Effect to handle movie selection after movies are loaded (for edit mode)
  useEffect(() => {
    if (
      movies.length > 0 &&
      data.attached_movies &&
      data.attached_movies.length > 0 &&
      type === "edit"
    ) {
      const attachedMovie = data.attached_movies[0];
      const foundMovie = movies.find(
        movie => movie.movie_id === attachedMovie.movie_id
      );

      if (foundMovie) {
        setSelectedMovie(foundMovie);
        setShowtimes(foundMovie.show_times || []);

        // Set selected showtime if exists
        if (data.attached_showtimes && data.attached_showtimes.length > 0) {
          const attachedShowtime = data.attached_showtimes[0];
          const foundShowtime = foundMovie.show_times?.find(
            showtime => showtime.show_time_id === attachedShowtime.showtime_id
          );
          if (foundShowtime) {
            setSelectedShowtime(foundShowtime);
          }
        }
      }
    }
  }, [movies, data, type]);

  console.log("Current state:", {
    editedValues,
    selectedMovie,
    selectedShowtime,
    selectedTicketTypes,
    selectedDate,
    isOn
  });

  const handleMovieSelection = movie => {
    setSelectedMovie(movie);
    setShowtimes(movie ? movie.show_times : []);
    setSelectedShowtime(null);
    setTicketTypes([]);
    setSelectedTicketTypes([]);
  };

  const handleShowtimeSelection = showtime => {
    setSelectedShowtime(showtime);
    setSelectedTicketTypes([]);
  };

  const handleTicketTypeSelection = ticketId => {
    setSelectedTicketTypes(prev =>
      prev.includes(ticketId)
        ? prev.filter(id => id !== ticketId)
        : [...prev, ticketId]
    );
  };

  const handleSubmit = async e => {
    try {
      e.preventDefault();
      setError({ msg: "", isError: false });

      if (!editedValues.item) {
        setError({ msg: "Choose a title", isError: true });
        return;
      }
      if (!editedValues.items.length) {
        setError({ msg: "Choose at least one item", isError: true });
        return;
      }

      setLoading(true);

      const submitData = {
        rosetta_product_id: editedValues.item,
        cinema_id: cinemaId,
        combo_items: editedValues.items
      };

      // Add ticket-specific data if linking is enabled
      if (
        isOn &&
        selectedMovie &&
        selectedShowtime &&
        selectedTicketTypes.length > 0
      ) {
        submitData.ticketIds = selectedTicketTypes;
        submitData.movieId = selectedMovie.movie_id;
        submitData.showtimeIds = [selectedShowtime.show_time_id];
      }

      if (type === "edit") {
        // For edit, use the same structure as create but call the edit API
        const editData = {
          rosetta_product_id: editedValues.item,
          cinema_id: cinemaId,
          combo_items: editedValues.items,
          // Add ticket-specific data if linking is enabled
          ...(isOn &&
            selectedMovie &&
            selectedShowtime &&
            selectedTicketTypes.length > 0 && {
              ticketIds: selectedTicketTypes,
              movieId: selectedMovie.movie_id,
              showtimeIds: [selectedShowtime.show_time_id]
            })
        };

        await api.editComboDeal(
          editData,
          editedValues.combo_deal_id, // Use combo deal ID instead of rosetta_product_id
          `Bearer ${jwt}`
        );
      } else {
        await api.createComboDeal(submitData, `Bearer ${jwt}`);
      }

      setLoading(false);
      refreshList();
      close();
    } catch (e) {
      dispatch({
        type: ALERT_OPEN,
        payload: {
          severity: "error",
          message: getRequestError(e)
        }
      });
      setLoading(false);
    }
  };

  const formatShowtime = showTimeString => {
    return new Date(showTimeString).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true
    });
  };

  const submitData = {
    rosetta_product_id: editedValues.item,
    cinema_id: cinemaId,
    combo_items: editedValues.items
  };

  // Add ticket-specific data if linking is enabled
  if (
    isOn &&
    selectedMovie &&
    selectedShowtime &&
    selectedTicketTypes.length > 0
  ) {
    submitData.ticketIds = selectedTicketTypes;
    submitData.movieId = selectedMovie.movie_id;
    submitData.showtimeIds = [selectedShowtime.show_time_id];
  }
  console.log("submitData", submitData);

  return (
    <SlideIn
      open={open}
      onClose={resetOnClose}
      from="right"
      minWidth="70vw"
      style={{ padding: "20px" }}
    >
      <List className={classes.mainContainer}>
        <ListItem className={classes.container}>
          <GoBack onClick={resetOnClose} type="button" />
        </ListItem>
        <form onSubmit={handleSubmit}>
          <ListItem className={classes.container}>
            <Typography variant="h3">Combo Deal</Typography>
          </ListItem>
          <ListItem className={classes.container}>
            <div className={classes.section} style={{ marginRight: "50px" }}>
              {loadingRosettaConcessions ? (
                <CircularLoad show={true} />
              ) : (
                <>
                  <Typography variant="h4" className={classes.title}>
                    Title
                  </Typography>
                  <TextField
                    className={classes.txtField}
                    id="title"
                    select
                    variant="outlined"
                    margin="normal"
                    label="Choose Concesssion"
                    name="Select-Title"
                    value={editedValues?.item}
                    onChange={e => {
                      console.log(e.target.value);
                      setEditedValues({
                        ...editedValues,
                        item: e.target.value,
                        price:
                          rosettaConcessions.find(
                            item => item.product_id === e.target.value
                          )?.product_price ?? ""
                      });
                    }}
                    disabled={type === "edit"}
                    fullWidth
                    style={{ width: "100%" }}
                  >
                    {rosettaConcessions.map(item => (
                      <MenuItem key={item?.product_id} value={item?.product_id}>
                        {item.product_name}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Typography variant="h4" className={classes.title}>
                    Items
                  </Typography>
                  <Select
                    className={classes.txtField}
                    labelId="mutiple-name-label"
                    id="mutiple-name"
                    multiple
                    variant="outlined"
                    margin="normal"
                    placeholder="Choose Concesssions"
                    name="Select-Items"
                    value={editedValues.items}
                    onChange={e => {
                      setEditedValues({
                        ...editedValues,
                        items: e.target.value
                      });
                    }}
                    fullWidth
                    style={{ width: "100%" }}
                  >
                    {rosettaConcessions.map(item => (
                      <MenuItem key={item?.product_id} value={item?.product_id}>
                        {item.product_name}
                      </MenuItem>
                    ))}
                  </Select>
                </>
              )}
              <Typography variant="h4" className={classes.title}>
                Price
              </Typography>
              <TextField
                name="lastName"
                label=""
                type="number"
                autoComplete="current-password"
                margin="normal"
                variant="outlined"
                className={classes.txtField}
                onChange={e =>
                  setEditedValues({ ...editedValues, price: e.target.value })
                }
                value={editedValues.price}
                required
                disabled={true}
                style={{ width: "100%" }}
              />
              {error.isError && (
                <div>
                  <div className={classes.error}>{error.msg}</div>
                </div>
              )}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "end",
                  gap: "10px",
                  marginBottom: "20px",
                  marginTop: "30px"
                }}
              >
                <p style={{ fontSize: "14px", color: "white" }}>
                  Allow concession combos to be linked directly to specific
                  tickets.
                </p>
                <button
                  onClick={() => setIsOn(!isOn)}
                  style={containerStyle}
                  type="button"
                >
                  <span style={thumbStyle} />
                </button>
              </div>
              {isOn && (
                <div>
                  {/* Date Selector */}
                  <Typography
                    variant="h4"
                    className={classes.title}
                    style={{ marginBottom: "10px" }}
                  >
                    Select Date
                  </Typography>
                  <TextField
                    className={classes.txtField}
                    select
                    variant="outlined"
                    margin="normal"
                    label="Choose Date"
                    value={selectedDate}
                    onChange={e => setSelectedDate(e.target.value)}
                    fullWidth
                    style={{ width: "100%", marginBottom: "20px" }}
                  >
                    {getNext7Days().map(dateOption => (
                      <MenuItem key={dateOption.value} value={dateOption.value}>
                        {dateOption.label}
                      </MenuItem>
                    ))}
                  </TextField>

                  {/* Movie Selector */}
                  <p
                    style={{
                      color: "white",
                      marginBottom: "22px",
                      fontSize: "20px"
                    }}
                  >
                    Select Movies
                    <span
                      style={{
                        color: "#979797",
                        fontSize: "12px",
                        marginLeft: "10px"
                      }}
                    >
                      Please select Showtime(s) and Ticket type(s)
                    </span>
                  </p>

                  {loadingMovies ? (
                    <CircularLoad show={true} />
                  ) : (
                    <TextField
                      className={classes.txtField}
                      select
                      variant="outlined"
                      margin="normal"
                      label="Select Movie"
                      value={selectedMovie?.movie_id || ""}
                      onChange={e => {
                        const movie = movies.find(
                          m => m.movie_id === e.target.value
                        );
                        handleMovieSelection(movie);
                      }}
                      fullWidth
                      style={{ width: "100%", marginBottom: "20px" }}
                    >
                      {movies.map(movie => (
                        <MenuItem key={movie.movie_id} value={movie.movie_id}>
                          {movie.movie_name}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}

                  {selectedMovie && (
                    <div
                      style={{
                        backgroundColor: "#242426",
                        padding: "20px",
                        borderRadius: "8px",
                        marginBottom: "20px"
                      }}
                    >
                      <div style={{ display: "flex", gap: "30px" }}>
                        <div style={{ flex: 1 }}>
                          <Typography
                            variant="h6"
                            style={{
                              color: "#888",
                              marginBottom: "15px",
                              fontSize: "16px",
                              fontWeight: "normal"
                            }}
                          >
                            Select a Showtime
                          </Typography>
                          <div>
                            {showtimes.map(showtime => (
                              <div
                                key={showtime.show_time_id}
                                style={{
                                  backgroundColor:
                                    selectedShowtime?.show_time_id ===
                                    showtime.show_time_id
                                      ? "#424242"
                                      : "transparent",
                                  padding: "15px 20px",
                                  marginBottom: "10px",
                                  borderRadius: "4px",
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  cursor: "pointer"
                                }}
                                onClick={() =>
                                  handleShowtimeSelection(showtime)
                                }
                              >
                                <span
                                  style={{ color: "white", fontSize: "16px" }}
                                >
                                  {formatShowtime(showtime.show_time)} -{" "}
                                  {showtime.screen_name}
                                </span>
                                {selectedShowtime?.show_time_id ===
                                showtime.show_time_id ? (
                                  <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <rect
                                      x="0.5"
                                      y="0.5"
                                      width="15"
                                      height="15"
                                      rx="3.5"
                                      fill="#F9F5FF"
                                    />
                                    <rect
                                      x="0.5"
                                      y="0.5"
                                      width="15"
                                      height="15"
                                      rx="3.5"
                                      stroke="#A233E2"
                                    />
                                    <path
                                      d="M12 5L6.5 10.5L4 8"
                                      stroke="#A233E2"
                                      strokeWidth="1.6666"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                ) : (
                                  <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <rect
                                      x="0.5"
                                      y="0.5"
                                      width="15"
                                      height="15"
                                      rx="3.5"
                                      fill="white"
                                    />
                                    <rect
                                      x="0.5"
                                      y="0.5"
                                      width="15"
                                      height="15"
                                      rx="3.5"
                                      stroke="#D0D5DD"
                                    />
                                  </svg>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>

                        {!selectedShowtime ? (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flex: 1
                            }}
                          >
                            <p style={{ color: "white" }}>
                              Please select a showtime to see the ticket types
                            </p>
                          </div>
                        ) : (
                          <div>
                            <Typography
                              variant="h6"
                              style={{
                                color: "#888",
                                marginBottom: "15px",
                                fontSize: "16px",
                                fontWeight: "normal"
                              }}
                            >
                              Select Ticket Type(s)
                            </Typography>
                            {loadingTickets ? (
                              <CircularLoad show={true} />
                            ) : (
                              <div>
                                {ticketTypes.map(ticketType => (
                                  <div
                                    key={ticketType.ticket_id}
                                    style={{
                                      backgroundColor: selectedTicketTypes.includes(
                                        ticketType.ticket_id
                                      )
                                        ? "#424242"
                                        : "transparent",
                                      padding: "15px 20px",
                                      marginBottom: "10px",
                                      borderRadius: "4px",
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      cursor: "pointer"
                                    }}
                                    onClick={() =>
                                      handleTicketTypeSelection(
                                        ticketType.ticket_id
                                      )
                                    }
                                  >
                                    <div>
                                      <span
                                        style={{
                                          color: "white",
                                          fontSize: "16px"
                                        }}
                                      >
                                        {ticketType.ticket_name}
                                      </span>
                                      <div
                                        style={{
                                          color: "#888",
                                          fontSize: "12px"
                                        }}
                                      >
                                        ₦
                                        {ticketType.display_price.toLocaleString()}
                                      </div>
                                    </div>
                                    {selectedTicketTypes.includes(
                                      ticketType.ticket_id
                                    ) ? (
                                      <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 16 16"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <rect
                                          x="0.5"
                                          y="0.5"
                                          width="15"
                                          height="15"
                                          rx="3.5"
                                          fill="#F9F5FF"
                                        />
                                        <rect
                                          x="0.5"
                                          y="0.5"
                                          width="15"
                                          height="15"
                                          rx="3.5"
                                          stroke="#A233E2"
                                        />
                                        <path
                                          d="M12 5L6.5 10.5L4 8"
                                          stroke="#A233E2"
                                          strokeWidth="1.6666"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                      </svg>
                                    ) : (
                                      <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 16 16"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <rect
                                          x="0.5"
                                          y="0.5"
                                          width="15"
                                          height="15"
                                          rx="3.5"
                                          fill="white"
                                        />
                                        <rect
                                          x="0.5"
                                          y="0.5"
                                          width="15"
                                          height="15"
                                          rx="3.5"
                                          stroke="#D0D5DD"
                                        />
                                      </svg>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div>
                <Button
                  variant="contained"
                  type="submit"
                  color="primary"
                  className={classes.createBtn}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : "Submit"}
                </Button>
              </div>
            </div>
          </ListItem>
        </form>
      </List>
    </SlideIn>
  );
};

export default ComboDealForm2;
